import React, { useState } from 'react';
import clsx from 'clsx';

interface TextAreaFieldWithAutoHeightResizeProps {
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLTextAreaElement>) => void;
  value: string;
  label: string;
  placeholder: string;
  name: string;
  size?: string;
  disabled?: boolean;
  error?: string;
  width?: number;
  isRequired?: boolean;
  rows?: number;
  className?: string;
}

const TextAreaFieldWithAutoHeightResize: React.FC<
  TextAreaFieldWithAutoHeightResizeProps
> = ({
  onChange = () => {},
  onBlur = () => {},
  value,
  label,
  placeholder,
  size,
  name,
  disabled = false,
  error,
  width,
  isRequired,
  rows = 3,
  className
}) => {
  const [minimumRows, setMinimumRows] = useState(rows);
  const computedRootStyle = () => {
    const style: { [key: string]: number | string } = {};

    if (width) {
      style.width = `${width}px`;
    }

    return style;
  };

  return (
    <div
      className={clsx({
        'w-full flex flex-col': true,
        'md:w-1/2': size === 'half'
      })}
      style={computedRootStyle()}
    >
      <span className="flex">
        <label
          className="mb-2 text-xs font-medium text-[#141921]"
          htmlFor={`grid-${name}`}
        >
          {label}
        </label>
        {isRequired && <p className="ml-[3px] text-xs text-yellow-500">*</p>}
      </span>
      <div>
        <div
          className={`h-fit w-full overflow-hidden rounded border border-gray-200 hover:border-blue-500 focus:border-gray-500 focus:bg-white focus:outline-none ${className}`}
        >
          <textarea
            className={clsx({
              'appearance-none block w-full border-none outline-none text-sm text-[#141921] font-medium overflow-y-auto padding-0 focus:ring-0':
                true,
              'resize-none': minimumRows !== undefined,
              'h-fit': !minimumRows
            })}
            rows={minimumRows}
            name={name}
            placeholder={placeholder}
            value={value}
            onChange={onChange}
            onBlur={onBlur}
            disabled={disabled}
            onKeyUp={(event: React.KeyboardEvent<HTMLTextAreaElement>) => {
              const lineBreakCount = (value.match(/\n/g) || []).length;
              if (event.key === 'Enter') {
                if (lineBreakCount >= 3) {
                  setMinimumRows(minimumRows + 1);
                }
              }
              if (event.key === 'Backspace') {
                if (minimumRows > 3 && lineBreakCount + 1 !== minimumRows) {
                  setMinimumRows(minimumRows - 1);
                }
              }
            }}
          />
        </div>
        {error && (
          <p className="mt-2 text-xs capitalize text-red-600">{error}</p>
        )}
      </div>
    </div>
  );
};

export default TextAreaFieldWithAutoHeightResize;
